import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Info } from "lucide-react";

export default function UserFormModal({ open, mode, user, roles, onClose, onSaved, presetRoleId }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role_id: "", reports_to_user_id: "", assigned_cities: "", employee_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // Fetch the eligible-managers list once the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingLists(true);
      try {
        const { data } = await api.get("/users/eligible-managers");
        if (!cancelled) setEligibleManagers(data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load managers");
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (mode === "edit" && user) {
      setForm({
        name: user.name,
        email: user.email,
        phone: user.phone,
        role_id: user.role_id,
        reports_to_user_id: user.reports_to_user_id || "",
        assigned_cities: (user.assigned_cities || []).join(", "),
        employee_id: user.employee_id || "",
      });
    } else {
      setForm({ name: "", email: "", phone: "", role_id: presetRoleId || "", reports_to_user_id: "", assigned_cities: "", employee_id: "" });
    }
  }, [open, mode, user, presetRoleId]);

  const rolesForDropdown = useMemo(() => roles, [roles]);
  const roleGroups = useMemo(() => ({
    director:  rolesForDropdown.filter(r => r.type === "director"),
    manager:   rolesForDropdown.filter(r => r.type === "manager"),
    executive: rolesForDropdown.filter(r => r.type === "executive"),
  }), [rolesForDropdown]);
  const roleById = useMemo(() => Object.fromEntries(roles.map((r) => [r.id, r])), [roles]);

  const selectedRole = roleById[form.role_id];
  const canPickReportsTo = !!selectedRole && selectedRole.type !== "director";

  // Group eligible managers: same-branch first (if the selected role is branch-scoped), then rest.
  const groupedManagers = useMemo(() => {
    const scopeBranch = selectedRole?.branch_id || null;
    const filtered = eligibleManagers.filter((m) => !user || m.id !== user.id);
    if (!scopeBranch) return { sameBranch: [], others: filtered };
    const same = [];
    const others = [];
    filtered.forEach((m) => (m.branch_id === scopeBranch ? same : others).push(m));
    return { sameBranch: same, others };
  }, [eligibleManagers, selectedRole, user]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.role_id) { setErr("Please select a role"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role_id: form.role_id,
        // Feb 2026 — Employee ID. Trimmed; empty string is sent on edit so the
        // backend can clear the field. On create, empty stays empty (backend
        // treats it as "not yet assigned").
        employee_id: (form.employee_id || "").trim(),
      };
      if (canPickReportsTo) {
        payload.reports_to_user_id = form.reports_to_user_id || null;
        if (mode === "edit" && !form.reports_to_user_id) {
          payload.clear_reports_to = true;
        }
      }
      // Feb 2026 · city scoping for CRM Executive accounts
      if (selectedRole?.name === "CRM Executive") {
        payload.assigned_cities = (form.assigned_cities || "")
          .split(",").map((c) => c.trim()).filter(Boolean);
      } else if (mode === "edit" && (user?.assigned_cities || []).length > 0) {
        // If role changed away from CRM Exec, clear any leftover city scope
        payload.assigned_cities = [];
      }
      if (mode === "edit") {
        await api.patch(`/users/${user.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const managerOptionLabel = (m) => `${m.name} · ${m.role_name} · ${m.branch_name}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="user-form-modal">
      <div className="w-full max-w-lg iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="user-form-close">
          <X className="w-4 h-4" />
        </button>

        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">
          {mode === "edit" ? "Edit Member" : "New Member"}
        </p>
        <h2 className="font-display text-2xl text-white mb-6">
          {mode === "edit" ? "Update user details" : "Add a new user"}
        </h2>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Full Name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} className="iz-input" required data-testid="user-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm(f => ({...f, email: e.target.value}))} className="iz-input" required data-testid="user-form-email" />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Phone</label>
              <input value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} className="iz-input" required data-testid="user-form-phone" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
              Employee ID
              <span className="text-gray-600 lowercase"> · unique across the org</span>
            </label>
            <input
              value={form.employee_id}
              onChange={(e) => setForm(f => ({...f, employee_id: e.target.value}))}
              className="iz-input"
              placeholder="e.g. INZ-1042"
              data-testid="user-form-employee-id"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Functional Role</label>
            <select
              value={form.role_id}
              onChange={(e) => setForm(f => ({...f, role_id: e.target.value, reports_to_user_id: ""}))}
              className="iz-input"
              required
              disabled={!!presetRoleId}
              data-testid="user-form-role"
            >
              <option value="">Select role…</option>
              <optgroup label="Director">
                {roleGroups.director.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </optgroup>
              <optgroup label="Managers">
                {roleGroups.manager.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </optgroup>
              <optgroup label="Executives">
                {roleGroups.executive.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Reports To — editable dropdown of every eligible manager, grouped by branch */}
          {canPickReportsTo && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
                Reports To
                {selectedRole?.branch_id && (
                  <span className="text-gray-600 lowercase"> (pick from same branch or global)</span>
                )}
              </label>
              <select
                value={form.reports_to_user_id}
                onChange={(e) => setForm(f => ({...f, reports_to_user_id: e.target.value}))}
                className="iz-input"
                data-testid="user-form-reports-to"
                disabled={loadingLists}
              >
                <option value="">— None (unassigned) —</option>
                {groupedManagers.sameBranch.length > 0 && (
                  <optgroup label="Same branch">
                    {groupedManagers.sameBranch.map(m => (
                      <option key={m.id} value={m.id}>{managerOptionLabel(m)}</option>
                    ))}
                  </optgroup>
                )}
                {groupedManagers.others.length > 0 && (
                  <optgroup label={groupedManagers.sameBranch.length ? "Other branches" : "All eligible managers"}>
                    {groupedManagers.others.map(m => (
                      <option key={m.id} value={m.id}>{managerOptionLabel(m)}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {eligibleManagers.length === 0 && !loadingLists && (
                <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                  <Info className="w-3 h-3" /> No Manager or Director users seeded yet.
                </p>
              )}
            </div>
          )}
          {selectedRole && !canPickReportsTo && (
            <div className="text-xs text-gray-400 bg-[#0f0f0f] border border-[#262626] rounded-md p-3">
              <span className="font-mono uppercase tracking-widest text-[10px] text-gray-500">Reports to · </span>
              <span className="text-[#d4af37]">Self (Director)</span>
            </div>
          )}

          {selectedRole?.name === "CRM Executive" && (
            <div data-testid="user-form-cities-container">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
                Assigned Cities
                <span className="text-gray-600 lowercase"> (comma-separated · leave blank for all)</span>
              </label>
              <input
                value={form.assigned_cities}
                onChange={(e) => setForm(f => ({...f, assigned_cities: e.target.value}))}
                className="iz-input"
                placeholder="e.g. Bangalore, Chennai, Tirupati"
                data-testid="user-form-cities"
              />
              <p className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                <Info className="w-3 h-3" /> This user will only see projects from these cities.
              </p>
            </div>
          )}

          {err && (
            <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2" data-testid="user-form-error">{err}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary" data-testid="user-form-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="user-form-submit">
              {saving ? "Saving…" : (mode === "edit" ? "Save changes" : "Create user")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
